import { useCallback } from 'react';
import { pick } from 'js/utils/ramda.util';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';

import * as server from '../server';
import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { LAST_SIX_MONTHS } from '../constants';
import { useParseSummaryData } from './useParseSummaryData';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../store';

export const useFetchSummary = () => {
    const isMounted = useIsMounted();
    const dispatch = useDynamicReducerDispatch();
    const parseSummaryData = useParseSummaryData();
    const {
        filters: { period }, startDate, endDate, page: { isPageChanged }, isPreventFetchByAnotherPeriod
    } = useDynamicReducerSelector(pick([
        'filters', 'startDate', 'endDate', 'page', 'isPreventFetchByAnotherPeriod'
    ]));
    const { showBeginEndBalanceEnabled, showTotalTransactionHistory, tkPrefix } = useComponentConfig();

    return useCallback(() => {
        if (!isMounted() || isPageChanged || isPreventFetchByAnotherPeriod
            || !showBeginEndBalanceEnabled && !showTotalTransactionHistory) {
            return;
        }

        dispatch(actions.updateProperty('isSummaryLoading', true));

        (async () => {
            try {
                let balanceSecondBulk;
                const { requestStartDate, requestEndDate } = helpers.getRequestDates({ period, startDate, endDate });

                const { data: { summary, endingBalances, startingBalances } } = await server.getBalanceHistory({
                    startDate: requestStartDate,
                    endDate: requestEndDate,
                    showTotalTransactionHistory,
                    showBeginEndBalanceEnabled
                });

                if (period === LAST_SIX_MONTHS) {
                    const {
                        startDate: startDateSecondBulk, endDate: endDateSecondBulk
                    } = helpers.getSecondBulkForSixMonthsDates();

                    balanceSecondBulk = await server.getBalanceHistory({
                        startDate: startDateSecondBulk,
                        endDate: endDateSecondBulk,
                        showTotalTransactionHistory,
                        showBeginEndBalanceEnabled
                    });
                }

                const summaryData = parseSummaryData({
                    balanceFirstBulk: { summary, endingBalances, startingBalances },
                    balanceSecondBulk: balanceSecondBulk?.data
                });

                isMounted() && dispatch(actions.updateSummaryData(summaryData));
            } catch (error) {
                isMounted() && dispatch(actions.fetchFail(helpers.parseError(error, tkPrefix)));
            } finally {
                isMounted() && dispatch(actions.updateProperty('isSummaryLoading', false));
            }
        })();
    }, [
        period, tkPrefix, dispatch, isPreventFetchByAnotherPeriod,
        isMounted, isPageChanged, startDate, endDate,
        parseSummaryData, showBeginEndBalanceEnabled, showTotalTransactionHistory
    ]);
};

